import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import SystemHeader from '../SystemHeader';
import { DailyReportAPI } from '../../services/feature-api';
import { getFeatureConfig } from '../../config/featureDisplayConfig';
import DailyReportChecklist from './daily-report-checklist';
import DailyReportSummary from './daily-report-summary';

// Chép từ PE_Form/daily-report/daily-report-form.js. Bỏ: ghép việc khách lẻ, nút hướng dẫn, số liệu reels/thị trường ở màn tóm tắt (QD-06).
const config = getFeatureConfig('daily-report');
const accent = config?.color || '#2563eb';

const parseChecklist = (raw) => {
  if (!raw) return [];
  if (typeof raw === 'string') {
    try { const p = JSON.parse(raw); if (Array.isArray(p)) return p; } catch {}
    return raw.split('\n').filter(Boolean).map((t) => ({ text: t.replace(/^\d+\.\s*/, ''), done: false }));
  }
  return Array.isArray(raw) ? raw : [];
};

const DailyReportForm = ({ userInfo, accessToken, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData || null;
  const today = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; })();
  const fmtDateFull = (d) => { const p = d?.split('-'); return p ? `${p[2]}/${p[1]}/${p[0]}` : ''; };

  // Báo cáo ngày cũ chỉ xem, không sửa — 1 người chỉ sửa được báo cáo HÔM NAY
  const isViewingOld = editData?.date && editData.date !== today;

  const [items, setItems] = useState(() => parseChecklist(editData?.checklist));
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', msg: '' });
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [existingReport, setExistingReport] = useState(editData || null);
  const [loaded, setLoaded] = useState(false);

  const loadToday = useCallback(async () => {
    if (!accessToken || isViewingOld) { setLoaded(true); return; }
    try {
      const [y, m] = today.split('-');
      const result = await DailyReportAPI.getList(accessToken, { month: m, year: y });
      const rows = result.data?.rows || result.data?.items || [];
      const existing = rows.find((r) => r.date === today);
      if (existing) { setExistingReport(existing); setItems(parseChecklist(existing.checklist)); }
    } catch {}
    setLoaded(true);
  }, [accessToken, today, isViewingOld]);
  useEffect(() => { loadToday(); }, [loadToday]);

  const isUpdate = !isViewingOld && Boolean(existingReport);

  const copyFromPrevious = async () => {
    if (!accessToken) return;
    try {
      const [y, m] = today.split('-');
      const result = await DailyReportAPI.getList(accessToken, { month: m, year: y });
      const rows = (result.data?.rows || result.data?.items || []).filter((r) => r.date && r.date < today).sort((a, b) => b.date.localeCompare(a.date));
      if (rows.length > 0) {
        const prev = parseChecklist(rows[0].checklist);
        setItems(prev.map((it) => ({ ...it, done: false })));
        setFeedback({ type: 'info', msg: `📋 Đã lấy ${prev.length} việc từ ngày trước` });
      } else setFeedback({ type: 'warn', msg: 'Không có báo cáo trước đó' });
    } catch (e) { setFeedback({ type: 'error', msg: e.message }); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViewingOld) return;
    if (items.length === 0) { setFeedback({ type: 'error', msg: 'Thêm ít nhất 1 công việc' }); return; }
    setSubmitting(true); setFeedback({ type: '', msg: '' });
    try {
      const payload = { date: today, checklist: JSON.stringify(items) };
      const id = existingReport?.id || editData?.id;
      const result = id ? await DailyReportAPI.update(accessToken, id, payload) : await DailyReportAPI.create(accessToken, payload);
      if (!result.success) throw new Error(result.error || 'Lỗi');
      if (result.data?.id) setExistingReport(result.data);
      const now = new Date();
      setSummaryData({ date: today, time: now.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }), items, userName: userInfo?.facebook || userInfo?.name || 'Bạn', userAvatar: userInfo?.avatar, stats: {} });
      setShowSummary(true);
    } catch (err) { setFeedback({ type: 'error', msg: err.message }); }
    finally { setSubmitting(false); }
  };

  if (showSummary && summaryData) {
    return <DailyReportSummary summaryData={summaryData} accent={accent} config={config} userInfo={userInfo} onLogout={onLogout}
      onBack={() => navigate(config.path)} onEdit={() => setShowSummary(false)} />;
  }

  const NutQuayLai = () => (
    <div className="flex items-center gap-2 px-4 pt-2 pb-1 bg-white">
      <button onClick={() => navigate(config.path)} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ color: accent, background: accent + '15' }}>
        <ArrowLeft size={14} /> Quay lại
      </button>
    </div>
  );

  if (isViewingOld) {
    const viewItems = parseChecklist(editData?.checklist);
    const vDone = viewItems.filter((it) => it.done).length;
    const vTotal = viewItems.length;
    const vPct = vTotal > 0 ? Math.round((vDone / vTotal) * 100) : 0;
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <SystemHeader userInfo={userInfo} onLogout={onLogout} pageTitle={config.name} />
        <div className="flex-1 max-w-2xl w-full mx-auto flex flex-col">
          <NutQuayLai />
          <div className="px-4 py-3 bg-white border-b border-gray-200">
            <div className="text-2xl font-black text-gray-800">📅 {fmtDateFull(editData.date)}</div>
            <div className="text-xs mt-0.5 text-gray-400">👁 Chế độ xem — báo cáo ngày cũ (chỉ đọc)</div>
          </div>
          <div className="px-4 py-3 space-y-3">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
                <span className="text-sm font-bold text-gray-700">Công việc</span>
                <div className="ml-auto flex items-center gap-2">
                  <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${vPct}%`, background: vPct === 100 ? '#16a34a' : accent }} />
                  </div>
                  <span className="text-xs font-bold" style={{ color: vPct === 100 ? '#16a34a' : accent }}>{vDone}/{vTotal}</span>
                </div>
              </div>
              <div className="px-3 py-2 space-y-1">
                {viewItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 py-1.5 px-2 rounded-lg" style={{ background: item.done ? '#f0fdf4' : '#fafafa' }}>
                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white flex-shrink-0" style={{ background: item.done ? '#16a34a' : '#9ca3af' }}>{idx + 1}</span>
                    {item.done && <span className="text-green-500 text-xs flex-shrink-0">✓</span>}
                    <span className={`text-sm flex-1 ${item.done ? 'text-green-700 font-medium' : 'text-gray-600'}`}>{item.text}</span>
                  </div>
                ))}
                {viewItems.length === 0 && <div className="text-center text-sm text-gray-400 py-4">Không có công việc</div>}
              </div>
            </div>
            <button onClick={() => navigate(config.path)} className="w-full py-3 rounded-xl text-white font-semibold text-sm shadow-md active:scale-[.97] transition-transform" style={{ background: accent }}>← Quay lại danh sách</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SystemHeader userInfo={userInfo} onLogout={onLogout} pageTitle={config.name} />
      <div className="flex-1 max-w-2xl w-full mx-auto flex flex-col">
        <NutQuayLai />
        <div className="px-4 py-3 bg-white border-b border-gray-200">
          <div className="text-2xl font-black text-gray-800">📅 {fmtDateFull(today)}</div>
          <div className="text-xs mt-0.5" style={{ color: accent }}>{isUpdate ? '📝 Cập nhật Mục tiêu CV hôm nay' : '✨ Tạo báo cáo mới'}</div>
        </div>
        {feedback.msg && (
          <div className={`mx-4 mt-2 p-2 text-xs rounded-lg border ${feedback.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : feedback.type === 'warn' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>{feedback.msg}</div>
        )}
        {loaded ? (
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-3 pb-6 space-y-3">
            <DailyReportChecklist items={items} onChange={setItems} accent={accent} onCopyPrevious={copyFromPrevious} />
            <button type="submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-bold text-sm shadow-lg disabled:opacity-60 active:scale-[.97] transition-all" style={{ background: submitting ? '#6b7280' : accent }}>
              {submitting ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Đang lưu...</> : <><Save size={15} /> {isUpdate ? 'Cập nhật' : 'Lưu báo cáo'}</>}
            </button>
          </form>
        ) : (
          <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent }} /></div>
        )}
      </div>
    </div>
  );
};

export default DailyReportForm;
